import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, API, compressImageToWebP, getToken } from "../lib/api";
import ZoomableImage from "../components/ZoomableImage";
import { toast } from "sonner";
import { ArrowLeft, Upload, Clock, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";

function TrendIcon({ trend }) {
  if (trend === "improving") return <TrendingUp className="text-[#4CAF50]" size={20} />;
  if (trend === "declining") return <TrendingDown className="text-[#D32F2F]" size={20} />;
  return <Minus className="text-[#FF8F00]" size={20} />;
}

export default function JournalEntryScreen() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const autoTriggeredRef = useRef(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/journals/${plantId}/entries`);
      setEntries(data.entries || []);
    } catch (err) {
      console.error("[Journal] load failed:", err);
    }
  }, [plantId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const image = await compressImageToWebP(file);
      await api.post(`/journals/${plantId}/entries`, { image });
      toast.success("Entry analysed and saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save entry");
    } finally { setBusy(false); }
  };

  const onDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/journals/${plantId}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Download failed (${res.status})`);
      }
      // Pull filename from Content-Disposition if present
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `journal_${plantId}.zip`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Journal archive downloaded");
    } catch (err) {
      toast.error(err.message || "Could not download archive");
    } finally {
      setDownloading(false);
    }
  }, [plantId]);

  // Auto-trigger download if arriving via the 48h-expiry notification deep link
  // (URL ?download=1). Runs only once per mount and only when we actually have
  // entries to bundle.
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (searchParams.get("download") !== "1") return;
    if (entries.length === 0) return;
    autoTriggeredRef.current = true;
    toast.info("Preparing your journal archive…");
    onDownload();
    // Clean the URL so a refresh doesn't re-trigger
    setSearchParams({}, { replace: true });
  }, [entries.length, searchParams, setSearchParams, onDownload]);

  const latest = entries[0];
  const previous = entries[1];

  const hoursLeft = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.round(ms / 3600000));
  };

  return (
    <Shell section="journal_entry">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigate("/journal")} className="flex items-center gap-1 text-[#4CAF50] text-sm hover:underline">
          <ArrowLeft size={14} /> Journal
        </button>
        {entries.length > 0 && (
          <button
            onClick={onDownload}
            disabled={downloading}
            className="btn-outline inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="journal-download-zip"
          >
            <Download size={13} />
            {downloading ? "Preparing…" : "Download ZIP"}
          </button>
        )}
      </div>

      <div className="glass-card p-5 mb-5 fade-up" data-testid="upload-zone">
        <h2 className="text-lg font-bold mb-3">This week's photo</h2>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full py-10 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2"
          style={{ borderColor: "rgba(76,175,80,0.4)", background: "rgba(76,175,80,0.04)" }}
          data-testid="journal-upload-button"
        >
          <Upload size={26} className="text-[#4CAF50]" />
          <p className="text-sm font-semibold">{busy ? "Analysing…" : "Upload weekly photo"}</p>
          <p className="text-xs text-muted-soft">AI will compare with last week</p>
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
      </div>

      {latest && (
        <div className="glass-card-strong p-5 mb-4 fade-up" data-testid="latest-entry">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-soft">AI summary</p>
            <span className="glass-pill amber inline-flex items-center gap-1">
              <Clock size={11} /> Expires in {hoursLeft(latest.expires_at)}h
            </span>
          </div>

          {previous && latest.image_data && previous.image_data && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div>
                <p className="text-[10px] uppercase text-muted-soft mb-1">Last week</p>
                <div className="rounded-lg overflow-hidden">
                  <ZoomableImage src={previous.image_data} alt="Last week" thumbHeight={150} testid="journal-prev-image" />
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase text-muted-soft mb-1">This week</p>
                <div className="rounded-lg overflow-hidden">
                  <ZoomableImage src={latest.image_data} alt="This week" thumbHeight={150} testid="journal-latest-image" />
                </div>
              </div>
            </div>
          )}

          {!previous && latest.image_data && (
            <div className="rounded-lg overflow-hidden mb-4">
              <ZoomableImage src={latest.image_data} alt="This week" thumbHeight={220} testid="journal-single-image" />
            </div>
          )}

          <div className="flex items-center gap-3 mb-3">
            <TrendIcon trend={latest.ai_report?.overall_trend} />
            <div>
              <p className="text-sm font-semibold capitalize">{latest.ai_report?.overall_trend || "stable"}</p>
              <p className="text-xs text-muted-soft">{latest.ai_report?.health_score_change}</p>
            </div>
          </div>

          {latest.ai_report?.key_observations?.length > 0 && (
            <div className="mb-4">
              <p className="text-[11px] uppercase text-muted-soft mb-2">Key observations</p>
              <ul className="text-sm text-soft space-y-1">
                {latest.ai_report.key_observations.map((o, i) => (
                  <li key={i} className="flex gap-2"><span className="text-[#4CAF50]">•</span>{o}</li>
                ))}
              </ul>
            </div>
          )}

          {latest.ai_report?.care_adjustments?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase text-muted-soft mb-2">Care adjustments</p>
              <div className="space-y-2">
                {latest.ai_report.care_adjustments.map((c, i) => (
                  <div
                    key={i}
                    className="glass-card p-3 border-l-4"
                    style={{
                      borderLeftColor:
                        c.urgency === "immediate" ? "#D32F2F" : c.urgency === "this_week" ? "#FF8F00" : "#4CAF50",
                    }}
                  >
                    <p className="text-sm font-semibold">{c.action}</p>
                    <p className="text-xs text-muted-soft mt-1">{c.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {latest.ai_report?.growth_summary && (
            <p className="mt-4 text-sm text-soft italic">"{latest.ai_report.growth_summary}"</p>
          )}
        </div>
      )}

      {entries.length > 1 && (
        <div>
          <h3 className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Previous weeks</h3>
          <div className="space-y-2">
            {entries.slice(1).map((e, idx) => (
              <div key={e.entry_id} className="glass-card p-3 flex items-center gap-3" data-testid={`previous-entry-${idx}`}>
                {e.image_data && <img src={e.image_data} className="w-12 h-12 rounded-lg object-cover" alt="" />}
                <div className="flex-1">
                  <p className="text-sm font-semibold capitalize">{e.ai_report?.overall_trend || "stable"}</p>
                  <p className="text-[11px] text-muted-soft">{new Date(e.created_at).toLocaleDateString()}</p>
                </div>
                <TrendIcon trend={e.ai_report?.overall_trend} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
